import { useCallback, useMemo, useState } from "react";
import { debounce } from "lodash";
import FilterBar from "components/FilterBar";
import DSAlert from "../../system-design/DSAlert";
import DSBox from "../../system-design/DSBox";
import DSButton from "../../system-design/DSButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSSegmentedTabs from "../../system-design/DSSegmentedTabs";
import DSTypography from "../../system-design/DSTypography";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import {
  Column,
  columnKey,
} from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { PopulatedDraftCollection } from "../../../api/collection";
import useApprovedCollectionsInfiniteQuery from "../hooks/usePublicCollectionsInfiniteQuery";
import useDownloadCollectionAssets from "../hooks/useDownloadCollectionAssets";
import CollectionsListAvatar from "./CollectionsListAvatar";
import CollectionDialog from "./CollectionDialog";

type DraftState = "draft" | "rejected";

const STATE_OPTIONS: { label: string; value: DraftState }[] = [
  { label: "Draft", value: "draft" },
  { label: "Rejected", value: "rejected" },
];

const PAGE_SIZE = 20;

const COLUMNS: Column<PopulatedDraftCollection>[] = [
  { field: "name", label: "Name", sortable: true },
  { field: "coverUrl", label: "Cover" },
  { field: "creator", label: "Creator" },
  // Not a stored field: the API counts `klydoIds` and sorts on `klydoCount`.
  {
    field: "klydoIds",
    sortField: "klydoCount",
    label: "Klydos",
    sortable: true,
  },
  { field: "updatedAt", label: "Last updated", sortable: true },
  { field: "id", label: "Assets" },
];

// Collections that were saved but never sent to review (state "draft"), or
// were sent back to the artist ("rejected"). Unlike "Collection In Progress"
// (a manual ETA tracker) this lists every one of them.
const DraftCollections = () => {
  const [state, setState] = useState<DraftState>("draft");
  const [textSearch, setTextSearch] = useState("");
  const [sort, setSort] = useState<Record<string, 1 | -1>>({});
  const [chosenCollection, setChosenCollection] = useState("");

  const debouncedSetSearch = useMemo(() => debounce(setTextSearch, 500), []);

  const filter = useMemo(
    () => (state === "draft" ? { draft: true } : { rejected: true }),
    [state],
  );

  const {
    data: collections,
    fetchNextPage,
    isLoading,
  } = useApprovedCollectionsInfiniteQuery(
    textSearch,
    PAGE_SIZE,
    true,
    sort,
    filter,
    "draft",
  );

  const {
    mutate: download,
    isPending: isDownloading,
    variables: downloadingId,
    error: downloadError,
    reset: resetDownload,
  } = useDownloadCollectionAssets();

  const handleSort = useCallback((field: string) => {
    setSort((prev) =>
      prev[field] === 1
        ? { [field]: -1 }
        : prev[field] === -1
          ? {}
          : { [field]: 1 },
    );
  }, []);

  const rows = collections as unknown as PopulatedDraftCollection[] | undefined;

  const buildRow = (
    collection: PopulatedDraftCollection,
    columns: Column<PopulatedDraftCollection>[],
  ) => (
    <>
      {columns.map((col) => {
        const key = columnKey(col);
        return (
          <DSTableCell key={key} style={{ whiteSpace: "nowrap" }}>
            {key === "coverUrl" ? (
              <CollectionsListAvatar collectionCover={collection.coverUrl} />
            ) : key === "creator" ? (
              (collection.creator?.name ?? "N/A")
            ) : key === "klydoCount" ? (
              (collection.klydoIds?.length ?? 0)
            ) : key === "updatedAt" ? (
              collection.updatedAt ? (
                new Date(collection.updatedAt).toLocaleDateString()
              ) : (
                "-"
              )
            ) : key === "id" ? (
              <DSButton
                size="small"
                variant="outlined"
                disabled={isDownloading}
                onClick={(e) => {
                  // The row itself opens the collection; this must not.
                  e.stopPropagation();
                  download(collection.id);
                }}
                startIcon={
                  isDownloading && downloadingId === collection.id ? (
                    <DSCircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Download
              </DSButton>
            ) : (
              String(collection[col.field] ?? "")
            )}
          </DSTableCell>
        );
      })}
    </>
  );

  return (
    <>
      <DSBox sx={{ mb: 2 }}>
        <DSSegmentedTabs
          size="small"
          options={STATE_OPTIONS}
          value={state}
          onChange={(value) => {
            if (value) {
              setState(value);
              setSort({});
            }
          }}
        />
      </DSBox>
      <FilterBar
        search={{
          defaultValue: textSearch,
          onChange: debouncedSetSearch,
          placeholder: "Search collections, creator name or id",
        }}
        activeCount={0}
      />
      {downloadError && (
        <DSAlert severity="error" sx={{ mb: 2 }} onClose={resetDownload}>
          {(downloadError as { response?: { status?: number } }).response
            ?.status === 413
            ? "This collection's files are too large to zip."
            : "Failed to download the assets. Please try again."}
        </DSAlert>
      )}
      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : !rows?.length ? (
        <DSTypography variant="body1" textAlign="center" sx={{ mt: 4 }}>
          {state === "draft"
            ? "No draft collections (saved but not sent to review)."
            : "No rejected collections."}
        </DSTypography>
      ) : (
        <DSBaseTable
          columns={COLUMNS}
          data={rows}
          rowBuilder={buildRow}
          onPressRow={({ id }: PopulatedDraftCollection) =>
            setChosenCollection(id)
          }
          fetchMore={fetchNextPage}
          onSort={handleSort as (field: keyof PopulatedDraftCollection) => void}
          sortState={sort}
        />
      )}
      {chosenCollection && (
        <CollectionDialog
          fullWidth
          maxWidth="xl"
          collectionId={chosenCollection}
          open={!!chosenCollection}
          onClose={() => setChosenCollection("")}
        />
      )}
    </>
  );
};

export default DraftCollections;
